import React from 'react';

import { InactivityHandlerProps } from './types';

const events = [
    'click',
    'focus',
    'keydown',
    'wheel'
];

export const InactivityHandler: React.FC<InactivityHandlerProps> = (props) => {
    const {
        children,
        exemptedRoutes,
        inactive,
        inactivityWarningDuration,
        inactiveWarningDispatch,
        locationPath,
        logout,
        logoutOffsetDuration,
        userEventTriggeredDispatch
    } = props;

    const warningTimeoutRef = React.useRef<NodeJS.Timeout>();
    const logoutTimeoutRef = React.useRef<NodeJS.Timeout>();

    const handleWindowEvents = React.useCallback(() => {
        clearTimeout(warningTimeoutRef.current);
        userEventTriggeredDispatch();

        warningTimeoutRef.current = setTimeout(() => {
            inactiveWarningDispatch();

            logoutTimeoutRef.current = setTimeout(() => {
                logout();
            }, logoutOffsetDuration);
        }, inactivityWarningDuration);
    }, [
        inactiveWarningDispatch,
        inactivityWarningDuration,
        logout,
        logoutOffsetDuration,
        userEventTriggeredDispatch
    ]);

    const removeWindowEvents = React.useCallback(() => {
        events.forEach((windowEvent) => {
            window.removeEventListener(windowEvent, handleWindowEvents);
        });
    }, [handleWindowEvents]);

    const addWindowEvents = React.useCallback(() => {
        events.forEach((windowEvent) => {
            window.addEventListener(windowEvent, handleWindowEvents);
        });
    }, [handleWindowEvents]);

    React.useEffect(() => {
        addWindowEvents();

        return () => {
            removeWindowEvents();
        };
    }, [
        addWindowEvents,
        removeWindowEvents
    ]);

    React.useEffect(() => {
        if (exemptedRoutes?.includes(locationPath)) {
            clearTimeout(warningTimeoutRef.current);
            clearTimeout(logoutTimeoutRef.current);

            return;
        }
        handleWindowEvents();
    }, [
        exemptedRoutes,
        handleWindowEvents,
        locationPath
    ]);

    React.useEffect(() => {
        if (inactive) {
            removeWindowEvents();
        }

        if (!inactive) {
            addWindowEvents();
            clearTimeout(logoutTimeoutRef.current);
        }
    }, [
        addWindowEvents,
        inactive,
        removeWindowEvents
    ]);

    return (
        <>
            {children}
        </>
    );
};